<!--

	An infinitely expandable vertical list.

-->
<template>
	<conditional :condition="!isEmpty" :message-otherwise="messageIfEmpty" class="mia-ExpandableList">
		<transition-group :name="transitionName">
			<div v-for="(item, index) of visibleItems" :key="keys[index]" class="mia-ExpandableList-item">
				<list-item-loader :index="index" :from="item" :item-props="itemProps" :conversions="conversions" :component="options.component" />
			</div>
		</transition-group>
		<slot name="load-more-button" :visible-length="visibleLength" :total-length="items.length" :on-more-click="onMoreClick">
			<span v-if="showLoadMore">
				<a class="btn btn-link mia-ExpandableList-button" @click.prevent.stop="onMoreClick">
					<slot name="load-more">
						<span v-tr="'loadMore'" />
					</slot>
				</a>
			</span>
		</slot>
		<template #replacement>
			<slot name="empty" />
		</template>
	</conditional>
</template>
<script lang="ts">
import { Component, toNative } from "vue-facing-decorator";
import Conditional from "./../../ConditionalReplacement.vue";
import ListItemLoader from "./ListItemLoader.vue";
import { VueListBase } from "./VueListBase";
import { type IExpandableOptions } from "./VueListOptions";

@Component({
	components: { Conditional, ListItemLoader },
})
export class ExpandableList<S extends string | number, T> extends VueListBase<S, T, IExpandableOptions> {
	visibleLength = 0;

	get transitionName() {
		return this.backendOptions.transitionName || "mia-ExpandableList-transition";
	}

	private get hasMore() {
		if (this.items == null) {
			return false;
		}
		return this.bufferedItems.totalCountEstimate > this.items.length;
	}

	get showLoadMore() {
		if (this.items == null) {
			return false;
		}
		if (this.items.length === 0) {
			return false;
		}
		return this.hasMore || this.visibleLength < this.items.length;
	}

	created() {
		this.visibleLength = this.options.pageSize;
		void this.preload(this.visibleLength);
	}

	get visibleItems(): S[] {
		if (this.items == null) {
			return [];
		}
		return this.items.slice(0, this.visibleLength);
	}

	onMoreClick() {
		this.visibleLength += this.options.pageSize;
		void this.preload(this.visibleLength);
	}
}

export default toNative(ExpandableList);
</script>
<style lang="scss">
.mia-ExpandableList {
	.mia-ExpandableList-button {
		width: 100%;
		text-align: center;
		padding: theme.$mia-padding-panel;
		background-color: theme.$mia-color-buttonMore;
		color: theme.$mia-color-buttonMore-font;

		&:hover,
		&:focus,
		&:active {
			text-decoration: none;
			background-color: theme.$mia-color-buttonMore-hover;
			color: theme.$mia-color-buttonMore-hover-font;
		}
	}
}

// mia-ExpandableList-transition
// Default transition for the "ExpandableList" component.
//
// - Uses the default class prefix of `mia-ExpandableList-transition`.
// - These styles are applied by Vue's <TransitionGroup> component.
//
// NOTE: This is a simple zoom-in-out animation to add/remove elements from lists.
// Not much thought has been put into this so feel free to modify it.
.mia-ExpandableList-transition-move,
.mia-ExpandableList-transition-enter-active,
.mia-ExpandableList-transition-leave-active {
	transition: all 0.5s ease;
}

.mia-ExpandableList-transition-enter-from,
.mia-ExpandableList-transition-leave-to {
	opacity: 0;
}

.mia-ExpandableList-transition-leave-active {
	animation: mia-animation-zoomOutDown .5s both;
	z-index: theme.$mia-zIndex-1--transitionItem;

	// Ensures leaving items are taken out of layout flow so that moving
	// animations can be calculated correctly (Prevents jumping items.)
	position: absolute;
}

// mia-ExpandableList-slide-transition
// Slide transition for the "ExpandableList" component.
.mia-ExpandableList-slide-transition-move,
.mia-ExpandableList-slide-transition-enter-active,
.mia-ExpandableList-slide-transition-leave-active {
	transition: all 0.5s ease;
}

.mia-ExpandableList-slide-transition-enter-from,
.mia-ExpandableList-slide-transition-leave-to {
	opacity: 0;
	transform: translateX(30px);
}

.mia-ExpandableList-slide-transition-leave-active {
	position: absolute;
}

@keyframes mia-animation-zoomOutDown {
	40% {
		transform: scale3d(.475, .475, .475) translate3d(0, -60px, 0);
		animation-timing-function: cubic-bezier(0.550, 0.055, 0.675, 0.190);
	}
	100% {
		transform: scale3d(.1, .1, .1) translate3d(0, 2000px, 0);
		transform-origin: center bottom;
		animation-timing-function: cubic-bezier(0.175, 0.885, 0.320, 1);
	}
}
</style>
